import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSecureItem } from '@/utils/localStorage';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  Calendar,
  AlertTriangle,
  Clock3,
  Eye,
  SortAsc,
  SortDesc,
  BarChart3,

  Shield,
  GraduationCap,

  Plus,
  ListTodo,
  X
} from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { useAuthStore } from "@/stores/authStore";
import { Progress } from "@/components/ui/progress";
import AddOnboardingDialog from '@/components/hiring/AddOnboardingDialog';
import ViewOnboardingDialog from '@/components/hiring/ViewOnboardingDialog';
import OnboardingTimelineDialog from '@/components/hiring/OnboardingTimelineDialog';
import { OnboardingTasksDialog } from '@/components/hiring/OnboardingTasksDialog';
import { useOnboarding } from '@/hooks/useOnboarding';
import { Onboarding } from '@/services/onboarding.service';

interface OnboardingListProps {
  limit?: number;
  showFilters?: boolean;
}

const OnboardingList: React.FC<OnboardingListProps> = ({ limit, showFilters = true }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasSubModulePermission, isLoading } = useAuthStore();

  // Check authentication
  useEffect(() => {
    // Only check authentication if not loading
    if (!isLoading) {
      const token = getSecureItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }
    }
  }, [navigate, isLoading]);

  // Check permissions
  const canCreateOnboarding = hasSubModulePermission('hiring', 'onboarding', 'create');
  const canEditOnboarding = hasSubModulePermission('hiring', 'onboarding', 'edit');
  const canDeleteOnboarding = hasSubModulePermission('hiring', 'onboarding', 'delete');

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Dialog states
  const [selectedOnboarding, setSelectedOnboarding] = useState<Onboarding | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingOnboarding, setEditingOnboarding] = useState<Onboarding | null>(null);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [timelineOnboarding, setTimelineOnboarding] = useState<Onboarding | null>(null);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);
  const [tasksOnboarding, setTasksOnboarding] = useState<Onboarding | null>(null);

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => { }
  });

  // Track initial fetch
  const initialFetchDone = useRef(false);

  // Use the onboarding hook
  const {
    onboardings,
    loading,
    fetchOnboardings,
    deleteOnboardingRecord,
    updateOnboardingRecord,
    updateOnboardingTaskStatus,
    calculateAndUpdateCompletion,
    calculateCompletionForAllOnboardings,
  } = useOnboarding();

  // Helper function to validate status based on completion
  const validateOnboardingStatus = (onboarding: Onboarding) => {
    // Calculate timeline completion


    // Calculate task completion and status
    let pendingTasks = 0;
    let inProgressTasks = 0;
    let completedTasks = 0;

    if (onboarding.tasks && Array.isArray(onboarding.tasks) && onboarding.tasks.length > 0) {
      pendingTasks = onboarding.tasks.filter((task: any) =>
        !task.status || task.status === 'pending'
      ).length;
      inProgressTasks = onboarding.tasks.filter((task: any) =>
        task.status === 'in_progress'
      ).length;
      completedTasks = onboarding.tasks.filter((task: any) =>
        task.status === 'completed'
      ).length;

    }

    // Check task status first to determine overall status
    if (onboarding.tasks && onboarding.tasks.length > 0) {
      // If there are pending tasks, status should be pending
      if (pendingTasks > 0) {
        return 'pending';
      }
      // If there are in-progress tasks, status should be in_progress
      else if (inProgressTasks > 0) {
        return 'in_progress';
      }
      // If all tasks are completed, check if overall completion is 100%
      else if (completedTasks === onboarding.tasks.length) {
        if (onboarding.completionPercentage === 100) {
          return 'completed';
        } else {
          return 'in_progress';
        }
      }
    } else {
      // No tasks assigned, check overall completion percentage
      if (onboarding.completionPercentage === 100) {
        return 'completed';
      } else if (onboarding.completionPercentage && onboarding.completionPercentage > 0) {
        return 'in_progress';
      } else {
        return 'pending';
      }
    }

    return onboarding.status;
  };

  // Auto-update status based on task and timeline completion
  const onboardingsToUpdate = useMemo(() => {
    return onboardings.filter(onboarding => {
      if (!onboarding._id) return false;

      // Use the new status validation logic
      const validatedStatus = validateOnboardingStatus(onboarding);

      // Only update if the validated status is different from current status
      return validatedStatus !== onboarding.status;
    });
  }, [onboardings]);

  // Track which onboardings have been auto-updated to prevent duplicate updates
  const autoUpdatedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (onboardingsToUpdate.length > 0) {
      const onboardingsToProcess = onboardingsToUpdate.filter(
        onboarding => !autoUpdatedRef.current.has(onboarding._id!)
      );

      if (onboardingsToProcess.length > 0) {
        // Use Promise.all to handle all updates concurrently
        Promise.all(
          onboardingsToProcess.map(async (onboarding) => {
            try {
              // Mark as being processed
              autoUpdatedRef.current.add(onboarding._id!);

              // Calculate completion and update status
              await calculateAndUpdateCompletion(onboarding._id!);

              return {
                success: true,
                onboarding,
                message: `Onboarding for ${onboarding.candidate?.name || 'Unknown'} status updated`
              };
            } catch (error) {
              // Remove from processed set if failed
              autoUpdatedRef.current.delete(onboarding._id!);
              return {
                success: false,
                onboarding,
                error: 'Failed to auto-update status'
              };
            }
          })
        ).then((results) => {
          // Show success/error toasts
          results.forEach((result) => {
            if (result.success) {
              toast({
                title: "Status Updated",
                description: result.message,
              });
            } else {
              toast({
                title: "Error",
                description: result.error,
                variant: "destructive",
              });
            }
          });
        });
      }
    }
  }, [onboardingsToUpdate, calculateAndUpdateCompletion, toast]);

  // Initial fetch
  useEffect(() => {
    if (!initialFetchDone.current) {
      fetchOnboardings({
        page: 1,
        limit: limit || 20
      });
      initialFetchDone.current = true;
    }
  }, [fetchOnboardings, limit]);

  // Listen for task creation events to refresh onboarding data
  useEffect(() => {
    const handleTaskCreated = (event: any) => {
      // Show immediate feedback
      toast({
        title: "🔄 Refreshing Data",
        description: "New task detected. Refreshing onboarding data...",
        duration: 2000,
      });

      // Refresh onboarding data immediately
      fetchOnboardings({
        page: 1,
        limit: limit || 20
      });

      // Show a toast notification if assignment was successful
      if (event.detail?.assignmentSuccess) {
        setTimeout(() => {
          toast({
            title: "✅ Task Assigned Successfully",
            description: "The new task has been automatically assigned to all onboarding records and is now visible.",
            duration: 4000,
          });
        }, 1000);
      }
    };

    const handleOnboardingRefresh = () => {


      // Refresh onboarding data immediately
      fetchOnboardings({
        page: 1,
        limit: limit || 20
      });
    };

    const handleForceOnboardingRefresh = () => {
      // Force refresh onboarding data with a slight delay to ensure backend has processed
      setTimeout(() => {
        fetchOnboardings({
          page: 1,
          limit: limit || 20
        });

        toast({
          title: "🔄 Refreshing Data",
          description: "Ensuring all tasks are visible...",
          duration: 2000,
        });
      }, 500);
    };

    window.addEventListener('task-created', handleTaskCreated);
    window.addEventListener('onboarding-refresh', handleOnboardingRefresh);
    window.addEventListener('force-onboarding-refresh', handleForceOnboardingRefresh);

    return () => {
      window.removeEventListener('task-created', handleTaskCreated);
      window.removeEventListener('onboarding-refresh', handleOnboardingRefresh);
      window.removeEventListener('force-onboarding-refresh', handleForceOnboardingRefresh);
    };
  }, [fetchOnboardings, limit]);

  // Recalculate completion percentages when onboardings are loaded
  useEffect(() => {
    if (onboardings.length > 0 && !loading) {
      // Automatically check and fix task assignments for all onboardings
      const checkAndFixTaskAssignments = async () => {
        try {
          const { checkAndFixAllTaskAssignments } = await import('@/services/onboarding.service');
          await checkAndFixAllTaskAssignments();

          // Recalculate completion for all onboardings
          await calculateCompletionForAllOnboardings();

          // Refresh the data to get updated information
          await fetchOnboardings();

        } catch (error) {
          console.error('❌ Error during automatic task assignment and completion calculation:', error);
        }
      };

      // Only run this once when onboardings are first loaded
      if (!initialFetchDone.current) {
        checkAndFixTaskAssignments();
      }
    }
  }, [onboardings, loading, calculateCompletionForAllOnboardings, fetchOnboardings]);

  // Get status color

  // Get status color for inline styles
  const getStatusColorValue = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return '#10b981'; // green-500
      case 'in_progress':
        return '#3b82f6'; // blue-500
      case 'pending':
        return '#f59e0b'; // yellow-500
      case 'cancelled':
        return '#ef4444'; // red-500
      default:
        return '#6b7280'; // gray-500
    }
  };

  // Format status for display
  const formatStatusDisplay = (status: string) => {
    if (!status) return 'Pending';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get status icon

  // Filter onboarding records
  const filteredOnboardings = useMemo(() => {
    let filtered = onboardings.map(onboarding => {
      // Apply status validation to fix any inconsistencies
      const validatedStatus = validateOnboardingStatus(onboarding);
      if (validatedStatus !== onboarding.status) {
        console.warn(`Status inconsistency detected for ${onboarding.candidate?.name || 'Unknown'}:`, {
          originalStatus: onboarding.status,
          validatedStatus: validatedStatus,
          completionPercentage: onboarding.completionPercentage
        });
        return { ...onboarding, status: validatedStatus };
      }

      return onboarding;
    });

    filtered = filtered.filter(onboarding => {
      const candidateName = onboarding.candidate?.name || '';
      const companyEmail = onboarding.companyEmail || '';

      const matchesSearch = searchQuery === '' ||
        candidateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        companyEmail.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' ||
        onboarding.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    });

    // Sort filtered results
    filtered.sort((a, b) => {
      let aValue = '';
      let bValue = '';

      switch (sortBy) {
        case 'candidateName':
          aValue = a.candidate?.name || '';
          bValue = b.candidate?.name || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'completionPercentage':
          return sortOrder === 'asc'
            ? (a.completionPercentage || 0) - (b.completionPercentage || 0)
            : (b.completionPercentage || 0) - (a.completionPercentage || 0);
        case 'createdAt':
        default:
          aValue = a.createdAt || '';
          bValue = b.createdAt || '';
          break;
      }

      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    return limit ? filtered.slice(0, limit) : filtered;
  }, [onboardings, searchQuery, statusFilter, sortBy, sortOrder, limit]);

  // Calculate statistics

  // Handle actions
  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setSortBy('createdAt');
    setSortOrder('desc');
  };

  const handleView = (onboarding: Onboarding) => {
    setSelectedOnboarding(onboarding);
    setViewDialogOpen(true);
  };

  const handleEdit = (onboarding: Onboarding) => {
    // Reset the editing state first to ensure clean state
    setEditingOnboarding(null);

    // Use setTimeout to ensure the state is reset before setting the new onboarding
    setTimeout(() => {
      setEditingOnboarding(onboarding);
      setAddDialogOpen(true);
    }, 0);
  };

  const handleTimeline = (onboarding: Onboarding) => {
    setTimelineOnboarding(onboarding);
    setTimelineDialogOpen(true);
  };

  const handleViewTasks = (onboarding: Onboarding) => {
    setTasksOnboarding(onboarding);
    setTasksDialogOpen(true);
  };

  // Handle refresh of selected onboarding data
  const handleViewOnboardingRefresh = async () => {
    if (selectedOnboarding?._id) {
      try {
        const { getOnboardingById } = await import('@/services/onboarding.service');
        const response = await getOnboardingById(selectedOnboarding._id);
        const freshData = response.data?.data || response.data || null;

        if (freshData) {
          setSelectedOnboarding(freshData);
        }
      } catch (error) {
        console.error('Error refreshing selected onboarding:', error);
      }
    }
  };

  const handleTaskStatusUpdate = async (taskId: string, status: string) => {
    if (!tasksOnboarding?._id) return;

    try {
      await updateOnboardingTaskStatus(tasksOnboarding._id, taskId, status);

      // Recalculate completion percentage after task status update
      await calculateAndUpdateCompletion(tasksOnboarding._id);

      toast({
        title: "Success",
        description: "Task status updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
    }
  };

  const handleAssignTasks = async (onboardingId: string) => {
    try {
      const { assignTasksToExistingOnboarding } = await import('@/services/onboarding.service');
      await assignTasksToExistingOnboarding(onboardingId);

      // Recalculate completion percentage after assigning tasks
      await calculateAndUpdateCompletion(onboardingId);

      toast({
        title: "Success",
        description: "Tasks assigned successfully",
      });
      // Refresh the onboarding list
      await fetchOnboardings();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to assign tasks",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (onboarding: Onboarding) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Onboarding Record',
      message: `Are you sure you want to delete the onboarding record for ${onboarding.candidate?.name}? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteOnboardingRecord(onboarding._id!);
          toast({
            title: "Success",
            description: "Onboarding record deleted successfully",
          });
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to delete onboarding record",
            variant: "destructive",
          });
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  };



  const handleOnboardingEdit = async (updatedData: any) => {
    if (!editingOnboarding?._id) return;

    try {
      // Extract fileUrls from the data
      const { fileUrls, ...formData } = updatedData;

      // Format the data properly for the update service
      // Note: candidate field is excluded from updates, so we use the existing candidate data
      const updateData = {
        // Use the existing candidate data from editingOnboarding since we don't update candidate
        candidate: editingOnboarding.candidate,
        companyEmail: formData.companyEmail,
        identityProofs: formData.identityProofs || {
          aadhaarNumber: '',
          aadhaarDocUrl: '',
          panNumber: '',
          panDocUrl: ''
        },
        previousEmployerDocuments: formData.previousEmployerDocuments || {
          expLetterUrl: '',
          relievingLetterUrl: '',
          resignationAcceptanceUrl: '',
          firstMonthSalarySlipUrl: '',
          secondMonthSalarySlipUrl: '',
          thirdMonthSalarySlipUrl: ''
        },
        educationalCertificates: formData.educationalCertificates || [],
        tasks: formData.tasks || [],
        notes: formData.notes || '',
        fileUrls: fileUrls || {}
      };



      // Use the hook's updateOnboardingRecord function which will update local state
      await updateOnboardingRecord(editingOnboarding._id, updateData);

      // Reset the editing state
      setEditingOnboarding(null);
      setAddDialogOpen(false);

      // Don't immediately refresh - let the backend process the update first
      // The local state will be updated by the updateOnboardingRecord function

      // Update the selectedOnboarding state if it's the same onboarding that was edited
      if (selectedOnboarding && selectedOnboarding._id === editingOnboarding._id) {
        // Use the updated data that was sent to the backend instead of fetching fresh data
        const updatedOnboarding = {
          ...selectedOnboarding,
          ...updateData,
          // Ensure we use the updated data for documents
          previousEmployerDocuments: updateData.previousEmployerDocuments,
          identityProofs: updateData.identityProofs,
          educationalCertificates: updateData.educationalCertificates,
          notes: updateData.notes,
          companyEmail: updateData.companyEmail
        };
        setSelectedOnboarding(updatedOnboarding);
      }

      // Also update timeline and tasks onboarding if they're the same
      if (timelineOnboarding && timelineOnboarding._id === editingOnboarding._id) {
        const updatedOnboarding = {
          ...timelineOnboarding,
          ...updateData,
          previousEmployerDocuments: updateData.previousEmployerDocuments,
          identityProofs: updateData.identityProofs,
          educationalCertificates: updateData.educationalCertificates,
          notes: updateData.notes,
          companyEmail: updateData.companyEmail
        };
        setTimelineOnboarding(updatedOnboarding);
      }

      if (tasksOnboarding && tasksOnboarding._id === editingOnboarding._id) {
        const updatedOnboarding = {
          ...tasksOnboarding,
          ...updateData,
          previousEmployerDocuments: updateData.previousEmployerDocuments,
          identityProofs: updateData.identityProofs,
          educationalCertificates: updateData.educationalCertificates,
          notes: updateData.notes,
          companyEmail: updateData.companyEmail
        };
        setTasksOnboarding(updatedOnboarding);
      }

      toast({
        title: "Success",
        description: "Onboarding record updated successfully",
      });
    } catch (error: any) {
      console.error('Error updating onboarding:', error);

      let errorMessage = "Failed to update onboarding record. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Handle dialog close to reset editing state
  const handleDialogClose = (open: boolean) => {
    setAddDialogOpen(open);
    if (!open) {
      setEditingOnboarding(null);

      // Refresh the selected onboarding data if view dialog is open
      if (viewDialogOpen && selectedOnboarding?._id) {
        handleViewOnboardingRefresh();
      }
    }
  };

  // Debug: Log when editingOnboarding changes
  useEffect(() => {
    if (editingOnboarding) {
      // editingOnboarding changed
    }
  }, [editingOnboarding]);

  return (
    <div className="space-y-4 p-4 pt-1">
      {/* Header */}
      {showFilters && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Onboarding Management</h1>
            </div>
            <div className="flex items-center gap-3">
              {canCreateOnboarding && (
                <Button onClick={() => setAddDialogOpen(true)} className="bg-black hover:bg-gray-800 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Onboarding
                </Button>
              )}
            </div>
          </div>
          {/* Filters and Search */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-wrap gap-4">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by candidate name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="createdAt">Date Created</SelectItem>
                    <SelectItem value="candidateName">Candidate Name</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                    <SelectItem value="completionPercentage">Completion %</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                </Button>

                {/* Clear Filters Button */}
                {searchQuery && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleClearFilters}
                    className="bg-gray-50 hover:bg-gray-100 border-gray-200"
                    title="Clear all filters"
                  >
                    <X className="h-4 w-4 text-gray-600" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Loader (matches HiringSettings style) */}
      {loading && onboardings.length === 0 && (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Onboarding...</h2>
            <p className="text-gray-600">Please wait while we fetch your onboarding data.</p>
          </div>
        </div>
      )}

      {/* No onboarding records message (only when not loading) */}
      {(!loading && onboardings.length === 0 && filteredOnboardings.length === 0) && (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">
            No onboarding records found
          </p>
        </div>
      )}

      {/* Onboarding List (only when not loading and there are records) */}
      {(!loading || onboardings.length > 0) && filteredOnboardings.length !== 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOnboardings.map((onboarding) => {
            // Determine border color based on status
            let borderColor = '#f59e0b'; // default (pending)
            if ((onboarding.status || '').toLowerCase() === 'completed') borderColor = '#10b981';
            else if ((onboarding.status || '').toLowerCase() === 'in_progress') borderColor = '#3b82f6';
            else if ((onboarding.status || '').toLowerCase() === 'cancelled') borderColor = '#ef4444';

            return (
              <Card
                key={onboarding._id}
                className={`hover:shadow-md transition-shadow h-[320px] flex flex-col relative border-l-4 ${onboarding.status === 'cancelled' ? 'opacity-75 bg-gray-50' : ''}`}
                style={{ borderLeftColor: borderColor }}
              >
                <CardHeader className="pb-3 flex-shrink-0">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg leading-tight mb-1">
                        {(onboarding.candidate?.firstName && onboarding.candidate?.lastName)
                          ? `${onboarding.candidate.firstName} ${onboarding.candidate.lastName}`
                          : (onboarding.candidate?.name || 'Unknown Candidate')}
                      </CardTitle>
                      <CardDescription className="text-sm text-gray-600">
                        {onboarding.companyEmail || 'No email'}
                      </CardDescription>
                    </div>
                    <Badge
                      variant="outline"
                      className="border-current/20 text-xs shrink-0"
                      style={{
                        backgroundColor: getStatusColorValue(onboarding.status || 'pending') + '20',
                        color: getStatusColorValue(onboarding.status || 'pending'),
                        borderColor: getStatusColorValue(onboarding.status || 'pending') + '40'
                      }}
                    >
                      {formatStatusDisplay(onboarding.status || 'pending')}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="py-2 flex-1 flex flex-col overflow-hidden">
                  <div className="space-y-2 flex-shrink-0">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">
                        {onboarding.createdAt
                          ? new Date(onboarding.createdAt).toLocaleDateString()
                          : 'No date'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <BarChart3 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">
                        {onboarding.completionPercentage || 0}% Complete
                        <span className="text-xs text-gray-400 ml-1">(Timeline-based)</span>
                      </span>
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-2">
                      <Progress
                        value={onboarding.completionPercentage || 0}
                        indicatorColor={
                          (onboarding.status === 'completed')
                            ? 'bg-green-500'
                            : (onboarding.completionPercentage || 0) >= 80
                              ? 'bg-yellow-400'
                              : (onboarding.completionPercentage || 0) >= 40
                                ? 'bg-blue-400'
                                : 'bg-red-500'
                        }
                        className="h-2"
                      />
                    </div>
                    {/* Task Status Indicator */}
                    {onboarding.tasks && onboarding.tasks.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <ListTodo className="h-3 w-3" />
                        <span>
                          {onboarding.tasks.filter((task: any) => task.status === 'completed').length}/
                          {onboarding.tasks.length} tasks completed
                        </span>
                        {onboarding.tasks.filter((task: any) => task.status === 'pending').length > 0 && (
                          <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-600 border-yellow-200">
                            {onboarding.tasks.filter((task: any) => task.status === 'pending').length} pending
                          </Badge>
                        )}
                      </div>
                    )}
                    {/* Quick Info */}
                    <div className="flex flex-wrap gap-4 text-xs text-gray-500 mt-2">

                      {onboarding.identityProofs?.aadhaarNumber && (
                        <div className="flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          <span>ID Verified</span>
                        </div>
                      )}
                      {onboarding.educationalCertificates?.length ? (
                        <div className="flex items-center gap-1">
                          <GraduationCap className="h-3 w-3" />
                          <span>{onboarding.educationalCertificates.length} Certificates</span>
                        </div>
                      ) : null}

                    </div>
                  </div>
                </CardContent>
                <CardFooter className="grid grid-cols-3 gap-2 pt-2 pb-3 px-3 flex-shrink-0 mt-auto border-t border-gray-50">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs px-2 py-1 rounded-full bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700 hover:text-blue-800"
                    onClick={() => handleView(onboarding)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs px-2 py-1 rounded-full bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 hover:text-purple-800"
                    onClick={() => handleTimeline(onboarding)}
                  >
                    <Clock3 className="h-3 w-3 mr-1" />
                    Timeline
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs px-2 py-1 rounded-full bg-green-50 hover:bg-green-100 border-green-200 text-green-700 hover:text-green-800"
                    onClick={() => handleViewTasks(onboarding)}
                  >
                    <ListTodo className="h-3 w-3 mr-1" />
                    Tasks
                  </Button>
                </CardFooter>

                {/* Show assign tasks button if tasks array is empty */}
                {(!onboarding.tasks || onboarding.tasks.length === 0) && (
                  <div className="px-3 pb-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full h-8 text-xs px-2 py-1 rounded-full bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700 hover:text-orange-800"
                      onClick={() => handleAssignTasks(onboarding._id!)}
                    >
                      <ListTodo className="h-3 w-3 mr-1" />
                      Assign Tasks
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      <AddOnboardingDialog
        key={editingOnboarding?._id || 'add'} // Force re-render when switching between different onboarding records
        mode={editingOnboarding ? 'edit' : 'add'}
        onboarding={editingOnboarding ? {
          candidate: editingOnboarding.candidate || { _id: '', name: '' },
          companyEmail: editingOnboarding.companyEmail || '',
          previousEmployerDocuments: editingOnboarding.previousEmployerDocuments || {
            expLetterUrl: '',
            relievingLetterUrl: '',
            resignationAcceptanceUrl: '',
            firstMonthSalarySlipUrl: '',
            secondMonthSalarySlipUrl: '',
            thirdMonthSalarySlipUrl: ''
          },
          identityProofs: editingOnboarding.identityProofs || { aadhaarNumber: '', aadhaarDocUrl: '', panNumber: '', panDocUrl: '' },
          educationalCertificates: editingOnboarding.educationalCertificates || [],
          tasks: editingOnboarding.tasks || [],
          notes: editingOnboarding.notes || ''
        } : undefined}
        open={addDialogOpen}
        onOpenChange={handleDialogClose}
        onOnboardingEdit={handleOnboardingEdit}
        onRefresh={fetchOnboardings}
      />

      <ViewOnboardingDialog
        onboarding={selectedOnboarding}
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        canEditOnboarding={canEditOnboarding}
        canDeleteOnboarding={canDeleteOnboarding}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRefresh={handleViewOnboardingRefresh}
      />

      <OnboardingTimelineDialog
        onboarding={timelineOnboarding}
        open={timelineDialogOpen}
        onOpenChange={setTimelineDialogOpen}
      />

      <OnboardingTasksDialog
        onboarding={tasksOnboarding}
        open={tasksDialogOpen}
        onOpenChange={setTasksDialogOpen}
        onTaskStatusUpdate={handleTaskStatusUpdate}
        canEditOnboarding={canEditOnboarding}
      />

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmDialog.type === 'danger' && <AlertTriangle className="h-5 w-5 text-red-500" />}
              {confirmDialog.title}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">{confirmDialog.message}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            >
              Cancel
            </Button>
            <Button
              variant={confirmDialog.type === 'danger' ? 'destructive' : 'default'}
              onClick={confirmDialog.onConfirm}
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingList;
